// src/App.jsx
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';

// Páginas públicas
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import ProductsListPage from './pages/ProductsListPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import PaymentResultPage from './pages/PaymentResultPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Componentes
import Navbar from './components/Navbar';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';

// Panel de administración
import AdminLayout from './admin/AdminLayout';
import Dashboard from './admin/Dashboard';
import AdminProducts from './admin/Products';
import ProductCreate from './admin/ProductCreate';
import ProductEdit from './admin/ProductEdit';
import UserList from './admin/UserList';
import AdminOrders from './admin/AdminOrders';
import SiteSettings from './admin/SiteSettings';
import MetaPixel from './components/MetaPixel';
import api from './api/axios';
import { useEffect, useState } from 'react';

// Nuevo componente ProtectedRoute para reemplazar la lógica de redirección automática
import ProtectedRoute from './components/ProtectedRoute';

const PublicLayout = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow">
        <Outlet />
      </main>
    </div>
  );
};

function App() {
  const [pixelId, setPixelId] = useState('');
  useEffect(() => { api.get('/settings').then(({ data }) => setPixelId(data.settings?.metaPixelId || '')).catch(() => {}); }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <MetaPixel pixelId={pixelId} />
          <Routes>
            {/* Rutas públicas con Navbar */}
            <Route element={<PublicLayout />}>
              <Route index element={<HomePage />} />
              <Route path="/products" element={<ProductsListPage />} />
              <Route path="/products/:category" element={<ProductsListPage />} />
              <Route path="/product/:id" element={<ProductPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route 
                path="/checkout" 
                element={
                  <PrivateRoute>
                    <CheckoutPage />
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <PrivateRoute>
                    <ProfilePage />
                  </PrivateRoute>
                } 
              />
              
              <Route path="/payment/result" element={<PaymentResultPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              
              {/* Rutas de login/register protegidas para usuarios NO autenticados */}
              <Route 
                path="/login" 
                element={
                  <ProtectedRoute>
                    <LoginPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/register" 
                element={
                  <ProtectedRoute>
                    <RegisterPage />
                  </ProtectedRoute>
                } 
              />
            </Route>

            {/* Rutas protegidas para administración */}
            <Route 
              path="/admin" 
              element={
                <PrivateRoute>
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                </PrivateRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="products/new" element={<ProductCreate />} />
              <Route path="products/edit/:id" element={<ProductEdit />} />
              <Route path="users" element={<UserList />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="settings" element={<SiteSettings />} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Route>

            {/* Manejo de rutas no encontradas */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
